<template>
  <div
    class="pie-wrap"
    :style="wrapperStyle"      
  >
    <apexchart
      type="pie"
      :options="chartOptions"
      :series="series"
      :height="height"         
      :width="'100%'"        
    />
  </div>
</template>

<script>
import ApexCharts from 'vue3-apexcharts'

export default {
  name: 'PieApex',
  components: { apexchart: ApexCharts },

  props: {
    currentStatistic: { type: Object, default: () => ({}) },
    colors: {
      type: Array,
      default: () => [
        '#7bd8d4', '#2979FF', '#FFD600', '#FF1744', '#D500F9', '#FF6D00', '#009688'
      ]
    },
    showLegend: { type: Boolean, default: true },

    minWidth: { type: [Number, String], default: 200 },
    maxWidth: { type: [Number, String], default: 320 },

    // Высота графика — можно передать '100%' (чтобы подстраиваться под aspect-ratio),
    // или число в px (например 220)
    height: { type: [Number, String], default: '100%' }
  },

  computed: {
    // Нормализуем вход в строку с единицами (число -> px)
    normMin() {
      return typeof this.minWidth === 'number' ? `${this.minWidth}px` : String(this.minWidth)
    },
    normMax() {
      return typeof this.maxWidth === 'number' ? `${this.maxWidth}px` : String(this.maxWidth)
    },

    // Стиль контейнера: width = clamp(min, 100%, max)
    wrapperStyle() {
      return {
        width: `clamp(${this.normMin}, 100%, ${this.normMax})`
        // height управляется через aspect-ratio в CSS (чтобы всегда был квадрат)
      }
    },

    countsObject() {
      const stat = this.currentStatistic || {}
      if (stat.device_counts && typeof stat.device_counts === 'object') {
        return { ...stat.device_counts }
      }
      const list = Array.isArray(stat.list) ? stat.list : []
      const counts = {}
      for (const rec of list) {
        let t = null
        if (rec && typeof rec.device_type === 'string' && rec.device_type.trim() !== '') {
          t = rec.device_type.trim().toLowerCase()
        } else if (
          rec &&
          (rec.is_mobile === 1 || rec.is_mobile === true || String(rec.is_mobile) === '1')
        ) {
          t = 'mobile'
        } else {
          t = 'desktop'
        }
        counts[t] = (counts[t] || 0) + 1
      }
      return counts
    },

    labels() {
      const keys = Object.keys(this.countsObject)
      if (!keys.length) return ['No data']
      return keys.map(k => String(k))
    },

    series() {
      if (!this.labels.length || (this.labels.length === 1 && this.labels[0] === 'No data')) {
        return [1]
      }
      return this.labels.map(l => Number(this.countsObject[l] || 0))
    },

    colorsUsed() {
      const palette = Array.isArray(this.colors) ? this.colors : []
      if (!this.labels.length) return palette
      return this.labels.map((_, i) => palette[i % palette.length])
    },

    chartOptions() {
      return {
        labels: this.labels,
        colors: this.colorsUsed,
        legend: {
          show: this.showLegend,
          position: 'bottom',
          horizontalAlign: 'center',
          floating: false,
          offsetY: 8,
          labels: { useSeriesColors: true },
          markers: { width: 12, height: 12, radius: 6 },
          formatter: (label, opts) => {
            const idx = opts.seriesIndex
            const val = opts.w.globals.series[idx]
            return `${label} (${val})`
          }
        },
        tooltip: { y: { formatter: (val) => `${val}` } },
        dataLabels: {
          enabled: true,
          formatter: (val, opts) => {
            const value = opts.w.globals.series[opts.seriesIndex]
            return `${val.toFixed(1)}% (${value})`
          },
          style: { fontSize: '1rem' }
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: { width: 280 },
              legend: { position: 'bottom' }
            }
          }
        ],
        stroke: { width: 0 },
        chart: {
          // Задаём ширину/высоту в процентах — компонент возьмёт размеры контейнера.
          width: '100%',
          height: '100%',
          animations: { enabled: true, easing: 'easeinout', speed: 400 }
        }
      }
    }
  }
}
</script>

<style scoped>
.pie-wrap {
  /* центрируем и делаем квадратный контейнер:
     width управляется через inline style (clamp),
     а aspect-ratio гарантирует одинаковую высоту -> круг всегда круглый */
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  aspect-ratio: 1 / 1;     /* важно: сохраняет квадратную форму контейнера */
  box-sizing: border-box;
  padding: 0.25rem;        /* немного внутреннего отступа, чтобы круг не обрезался */
}

/* apexcharts контейнер должен занимать 100% ширины и высоты родителя */
.vue-apexcharts, 
::v-deep(.apexcharts-canvas svg) {
  width: 100% !important;
  height: 100% !important;
}

/* легенда — оставляем как у тебя, немного подправлено */
::v-deep(.apexcharts-legend) {
  width: 100%;
  margin-top: 1rem;
  box-sizing: border-box;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
  align-items: flex-start !important;
  gap: 0.5rem;
}
::v-deep(.apexcharts-legend-series) {
  display: flex;
  gap: 0.5rem;
}
::v-deep(.apexcharts-legend-text) {
  font-size: 1rem !important;
}
</style>
